import './floating-buttons.scss';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { ReactComponent as NaverBlogIcon } from '@/assets/icons/svg/naver-blog-pc.svg';
import { ReactComponent as DevelloIcon } from '@/assets/icons/svg/devello-pc.svg';
import { ReactComponent as InstagramIcon } from '@/assets/icons/svg/instagram-pc.svg';
import { ReactComponent as YoutubeIcon } from '@/assets/icons/svg/youtube-pc.svg';
import { ReactComponent as MenuIcon } from '@/assets/icons/svg/common/floating-plus.svg';
import { 유튜브_링크, 블로그_링크, 인스타그램_링크 } from '@/constants/links';
import { useRouter } from '@/hooks/useRouter';
import paths from '@/routes/paths';
import useDeviceSize from '@/hooks/useDeviceSize';

const AUTO_CLOSE_TIME = 4000; // 4초뒤 닫힘

const FloatingButtons = () => {
	const { deviceSize } = useDeviceSize();
	const [isOpen, setIsOpen] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);
	const toggle = useRef<HTMLButtonElement>(null);
	const reducedMotion = useReducedMotion();
	const router = useRouter();

	const handleLinkClick = (link: string) => {
		window.open(link, '_blank', 'noopener,noreferrer');
	};

	const isCompact = deviceSize !== 'pc';
	const buttonStep = deviceSize === 'mobile' ? 56 : 60;

	const buttonVariants = {
		hidden: {
			y: 0,
			opacity: 0,
			transition: {
				duration: reducedMotion ? 0 : 0.2
			}
		},
		visible: (index: number) => ({
			y: -buttonStep * (index + 1),
			opacity: 1,
			transition: {
				duration: reducedMotion ? 0 : 0.2
			}
		})
	};

	const buttons = [
		{
			id: 'inquiry',
			icon: <DevelloIcon className="devello" />,
			onClick: () => router.push(paths.inquiry),
			ariaLabel: '문의하기'
		},
		{
			id: 'blog',
			icon: <NaverBlogIcon className="naver-blog" />,
			onClick: () => handleLinkClick(블로그_링크),
			ariaLabel: '블로그로 이동'
		},
		{
			id: 'youtube',
			icon: <YoutubeIcon className="youtube" />,
			onClick: () => handleLinkClick(유튜브_링크),
			ariaLabel: '유튜브로 이동'
		},
		{
			id: 'instagram',
			icon: <InstagramIcon className="instagram" />,
			onClick: () => handleLinkClick(인스타그램_링크),
			ariaLabel: '인스타그램으로 이동'
		}
	];

	// Keep keyboard targets available while focus is inside the quick menu.
	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (isOpen && isCompact && !hasFocus) {
			timer = setTimeout(() => {
				setIsOpen(false);
			}, AUTO_CLOSE_TIME);
		}

		// 컴포넌트가 언마운트되거나 isOpen이 변경되기 전에 타이머 정리
		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [isOpen, isCompact, hasFocus]);

	return (
		<div
			className={`floating-buttons-wrapper${
				isCompact ? ' is-compact' : ''
			}`}
			role="group"
			aria-label="빠른 바로가기"
			onFocusCapture={() => setHasFocus(true)}
			onBlurCapture={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget))
					setHasFocus(false);
			}}
			onKeyDown={(event) => {
				if (event.key === 'Escape' && isOpen) {
					setIsOpen(false);
					toggle.current?.focus();
				}
			}}
		>
			{!isCompact ? (
				buttons.map((button, index) => (
					<button
						key={index}
						className="circle-button"
						onClick={button.onClick}
						data-analytics-cta={`floating_${button.id}`}
						data-analytics-label={button.ariaLabel}
						aria-label={button.ariaLabel}
					>
						{button.icon}
					</button>
				))
			) : (
				<>
					<AnimatePresence>
						{isOpen &&
							[...buttons].reverse().map((button, index) => (
								<motion.button
									key={index}
									className="circle-button"
									custom={index} // variants로 전달함.
									variants={buttonVariants} // custom에서 전달받음.
									initial="hidden"
									animate="visible"
									exit="hidden"
									onClick={button.onClick}
									data-analytics-cta={`floating_${button.id}`}
									data-analytics-label={button.ariaLabel}
									aria-label={button.ariaLabel}
								>
									{button.icon}
								</motion.button>
							))}
					</AnimatePresence>
					<motion.button
						ref={toggle}
						className="circle-button toggle-button"
						onClick={() => setIsOpen(!isOpen)}
						animate={{ rotate: isOpen ? 45 : 0 }}
						transition={{ duration: reducedMotion ? 0 : 0.2 }}
						aria-label="메뉴 열기/닫기"
						aria-expanded={isOpen}
					>
						<MenuIcon className="menu-icon" />
					</motion.button>
				</>
			)}
		</div>
	);
};

export default FloatingButtons;
